const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function request(path, { method = "GET", body, headers = {}, raw = false } = {}) {
  const opts = {
    method,
    credentials: "include",
    headers: { ...headers },
  };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_URL}${path}`, opts);
  if (raw) return res;
  if (res.status === 204) return null;

  let payload = null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    payload = await res.json().catch(() => null);
  }
  if (!res.ok) {
    const detail = payload?.detail || res.statusText || `HTTP ${res.status}`;
    throw new ApiError(detail, res.status, payload);
  }
  return payload;
}

export const api = {
  // Auth
  login: (username, password) =>
    request("/auth/login", { method: "POST", body: { username, password } }),
  logout: () => request("/auth/logout", { method: "POST" }),
  me: () => request("/auth/me"),

  // Users (admin)
  listUsers: () => request("/users"),
  createUser: (data) => request("/users", { method: "POST", body: data }),
  updateUser: (id, data) => request(`/users/${id}`, { method: "PUT", body: data }),
  resetUserPassword: (id, newPassword) =>
    request(`/users/${id}/reset-password`, { method: "POST", body: { new_password: newPassword } }),
  deleteUser: (id) => request(`/users/${id}`, { method: "DELETE" }),

  // Locations
  listLocations: () => request("/locations"),
  createLocation: (data) => request("/locations", { method: "POST", body: data }),
  updateLocation: (id, data) => request(`/locations/${id}`, { method: "PUT", body: data }),
  deleteLocation: (id) => request(`/locations/${id}`, { method: "DELETE" }),

  // Equipment (Modell)
  listEquipment: () => request("/equipment"),
  createEquipment: (data) => request("/equipment", { method: "POST", body: data }),
  updateEquipment: (id, data) => request(`/equipment/${id}`, { method: "PUT", body: data }),
  deleteEquipment: (id) => request(`/equipment/${id}`, { method: "DELETE" }),

  // Items
  listItems: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/items${qs ? `?${qs}` : ""}`);
  },
  getItem: (id) => request(`/items/${id}`),
  createItems: (data) => request("/items", { method: "POST", body: data }),
  updateItem: (id, data) => request(`/items/${id}`, { method: "PUT", body: data }),
  deleteItem: (id) => request(`/items/${id}`, { method: "DELETE" }),
  itemQrUrl: (id) => `${API_URL}/items/${id}/qr.png`,
  qrSheetUrl: () => `${API_URL}/items/qr-sheet`,
  scanItem: (token) => request(`/scan/${encodeURIComponent(token)}`),

  // Public self-service (no auth)
  publicCreateRental: (data) =>
    fetch(`${API_URL}/public/rentals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (r) => {
      const payload = await r.json().catch(() => null);
      if (!r.ok) throw new ApiError(payload?.detail || `HTTP ${r.status}`, r.status, payload);
      return payload;
    }),

  // Rentals
  listRentals: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/rentals${qs ? `?${qs}` : ""}`);
  },
  getRental: (id) => request(`/rentals/${id}`),
  createRental: (data) => request("/rentals", { method: "POST", body: data }),
  addItemsToRental: (id, data) => request(`/rentals/${id}/items`, { method: "POST", body: data }),
  updateRental: (id, data) => request(`/rentals/${id}`, { method: "PUT", body: data }),
  deleteRental: (id) => request(`/rentals/${id}`, { method: "DELETE" }),
  returnItem: (rentalEquipmentId) =>
    request(`/rental_equipment/${rentalEquipmentId}/return`, { method: "POST" }),

  // Admin
  endOfSeason: (confirmText) =>
    request("/admin/end-of-season", { method: "POST", body: { confirm_text: confirmText } }),
  exportData: () => `${API_URL}/export/data`,
  exportRapidmail: () => `${API_URL}/export/rapidmail`,
  exportQrCatalog: () => `${API_URL}/exports/qr-catalog.pdf`,
  recap: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v))
    ).toString();
    return request(`/admin/recap${qs ? `?${qs}` : ""}`);
  },
};

export { ApiError, API_URL };
